export const CRITICAL_NEWS_DOCUMENT_TYPE = 2;

export const CRITICAL_NEWS_LANGUAGES = ['English', 'French'];
export const CRITICAL_NEWS_LANGUAGE_CODES: Record<string, number> = {
    English: 1,
    French: 2,
};

const HOURS_PER_PERIOD = 12;
const MINUTES_STEP = 30;
const STEPS_PER_HOUR = 2;
const PAD_LENGTH = 2;

const pad = (value: number) => `${value}`.padStart(PAD_LENGTH, '0');

// 12-hour clock starting at 00:00, e.g. "00:00 AM" ... "11:30 AM", "00:00 PM" ... "11:30 PM".
export const CRITICAL_NEWS_TIME_OPTIONS: string[] = ['AM', 'PM'].flatMap(
    (period) =>
        Array.from(
            { length: HOURS_PER_PERIOD * STEPS_PER_HOUR },
            (_, index) =>
                `${pad(Math.floor(index / STEPS_PER_HOUR))}:${pad(
                    (index % STEPS_PER_HOUR) * MINUTES_STEP,
                )} ${period}`,
        ),
);

export const DEFAULT_START_TIME = '00:00:00';
export const DEFAULT_END_TIME = '23:59:59';

/** Converts a time option ("06:30 PM") to 24-hour "HH:MM:SS" ("18:30:00"). */
export const toNewsTime = (
    time: string,
    fallback = DEFAULT_START_TIME,
): string => {
    if (!time) return fallback;
    const [clock, period] = time.split(' ');
    const [hours, minutes] = clock.split(':').map(Number);
    const hours24 = period === 'PM' ? hours + HOURS_PER_PERIOD : hours;

    return `${pad(hours24)}:${pad(minutes)}:00`;
};

/**
 * Formats a picker date ("YYYY-MM-DD") as "MM/DD/YYYY", plus " HH:MM:SS" when a time
 * is given, without a Date object so no time zone conversion can shift the day.
 */
export const toNewsDate = (
    date: string,
    time?: string,
    fallback?: string,
): string => {
    const [year, month, day] = date.split('-');
    const formattedDate = `${month}/${day}/${year}`;

    return time || fallback
        ? `${formattedDate} ${toNewsTime(time ?? '', fallback)}`
        : formattedDate;
};

export const escapeHtml = (value: string) =>
    value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

const ALLOWED_TAGS = new Set([
    'p',
    'br',
    'strong',
    'em',
    'u',
    's',
    'h1',
    'h2',
    'h3',
    'ul',
    'ol',
    'li',
    'blockquote',
    'a',
]);

// Editors emit these equivalents; they are stored as the allowed tag.
const TAG_ALIASES: Record<string, string> = {
    b: 'strong',
    i: 'em',
    strike: 's',
    div: 'p',
};

// Dropped together with their content; other unknown tags keep their text.
const DROPPED_TAGS = new Set([
    'script',
    'style',
    'iframe',
    'object',
    'embed',
    'noscript',
    'template',
    'svg',
    'math',
    'textarea',
    'select',
    'button',
    'form',
    'input',
]);

export const ALLOWED_LINK_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/**
 * Returns the normalized URL when it is http, https or mailto; otherwise null.
 */
export const sanitizeLinkUrl = (value: string): string | null => {
    try {
        const url = new URL(value.trim());

        return ALLOWED_LINK_PROTOCOLS.includes(url.protocol) ? url.href : null;
    } catch {
        return null;
    }
};

const BOLD_FONT_WEIGHT = 600;

// Pasted content (Google Docs, Word, web pages) carries formatting as inline styles.
const getStyleFormats = (element: Element) => {
    const style = (element as HTMLElement).style;
    if (!style) {
        return {
            bold: false,
            italic: false,
            underline: false,
            strike: false,
            isNormalWeight: false,
        };
    }

    const weight = style.fontWeight;
    const decoration = `${style.textDecoration} ${style.getPropertyValue(
        'text-decoration-line',
    )}`;

    return {
        bold:
            weight === 'bold' ||
            weight === 'bolder' ||
            Number(weight) >= BOLD_FONT_WEIGHT,
        italic: ['italic', 'oblique'].includes(style.fontStyle),
        underline: decoration.includes('underline'),
        strike: decoration.includes('line-through'),
        isNormalWeight: weight === 'normal' || weight === '400',
    };
};

const applyStyleFormats = (
    tag: string,
    formats: ReturnType<typeof getStyleFormats>,
    content: string,
): string => {
    let result = content;

    if (formats.strike && tag !== 's') result = `<s>${result}</s>`;
    if (formats.underline && tag !== 'u') result = `<u>${result}</u>`;
    if (formats.italic && tag !== 'em') result = `<em>${result}</em>`;
    if (formats.bold && tag !== 'strong') result = `<strong>${result}</strong>`;

    return result;
};

const serializeChildren = (parent: Node): string =>
    Array.from(parent.childNodes).map(serializeNode).join('');

function serializeNode(node: Node): string {
    if (node.nodeType === TEXT_NODE) {
        return escapeHtml(node.textContent ?? '');
    }
    if (node.nodeType !== ELEMENT_NODE) {
        return '';
    }

    const element = node as Element;
    const sourceTag = element.tagName.toLowerCase();
    const tag = TAG_ALIASES[sourceTag] ?? sourceTag;

    if (DROPPED_TAGS.has(tag)) return '';
    if (tag === 'br') return '<br>';

    const formats = getStyleFormats(element);
    const inner = serializeChildren(element);

    // Google Docs wraps all pasted text in <b style="font-weight:normal">.
    if (tag === 'strong' && formats.isNormalWeight) return inner;

    const content = applyStyleFormats(tag, formats, inner);

    if (!ALLOWED_TAGS.has(tag)) return content;
    if (tag === 'a') {
        const href = sanitizeLinkUrl(element.getAttribute('href') ?? '');

        return href
            ? `<a href="${escapeHtml(
                  href,
              )}" target="_blank" rel="noopener noreferrer">${content}</a>`
            : content;
    }

    return `<${tag}>${content}</${tag}>`;
}

/**
 * Rebuilds the HTML from an allowlist so no script, event handler, style or
 * unsafe link can reach the API. Every attribute is dropped except link href.
 */
export const sanitizeNewsHtml = (html: string): string => {
    const { body } = new DOMParser().parseFromString(html ?? '', 'text/html');

    return serializeChildren(body);
};

export const isBodyEmpty = (html: string): boolean => {
    const { body } = new DOMParser().parseFromString(
        sanitizeNewsHtml(html),
        'text/html',
    );

    return !(body.textContent ?? '').trim();
};
